import { HazardPlusTotal } from "@/features/pe_aldd";
import { PeaSummaryResponse, PeaSummaryRow, Year, YearSeries } from "@/store/services/facades/types/pea";
import { Rcp } from "@/types/rcp";
import { normalizeDisplayZero } from "@/utils/number";

/** Summary row for the selected climate scenario + hazard. */
export const findSummaryRow = (
  summary: PeaSummaryResponse,
  rcp: Rcp,
  hazard: HazardPlusTotal
): PeaSummaryRow | undefined => summary.find((row) => row.climate_scenario === rcp && row.hazard === hazard);

/**
 * Raw sampled years → values, skipping non-finite entries. These are the points the chart plots:
 * annual for aggregate data, 5-yearly for point-level.
 */
export const toSampleMap = (series: YearSeries | null): Map<number, number> => {
  const samples = new Map<number, number>();
  if (!series) return samples;

  for (const key of Object.keys(series)) {
    const year = Number(key);
    const value = series[Number(key) as Year];
    if (Number.isFinite(year) && Number.isFinite(value)) samples.set(year, value);
  }
  return samples;
};

/**
 * Expand sparse samples to annual resolution, carrying each value forward until the next sample —
 * a 2025 point-level value fills 2026–2029, 2030 fills 2031–2034, and so on. Spans the first to
 * last sampled year only, never extrapolating. Avoided risk is an annual flow, so cumulative sums
 * must count every year; a no-op for already-annual aggregate data.
 */
export const forwardFillAnnual = (samples: Map<number, number>): Map<number, number> => {
  const filled = new Map<number, number>();
  if (samples.size === 0) return filled;

  const years = [...samples.keys()].sort((a, b) => a - b);
  let carried = samples.get(years[0]) as number;
  for (let year = years[0]; year <= years[years.length - 1]; year++) {
    if (samples.has(year)) carried = samples.get(year) as number;
    filled.set(year, carried);
  }
  return filled;
};

type AvoidedRiskArgs = {
  baselineSummary: PeaSummaryResponse;
  comparisonSummary: PeaSummaryResponse;
  rcp: Rcp;
  hazard: HazardPlusTotal;
  selectedYear: Year;
  discountRate: number;
  baseYear: number;
};

export type AvoidedRiskMetrics = {
  /** Nominal (undiscounted) avoided risk in the selected year. */
  annualAvoidedRisk: number;
  /** Annual Avoided Risk Value as a percentage of the asset's declared market value. */
  valueProtected: number | null;
  /** Σ nominal (undiscounted) avoided risk from present day through to the selected year. */
  cumulativeAvoidedRisk: number;
  /** Σ PV(t) of avoided risk from present day through to the selected year (max justifiable investment). */
  cumulativeBreakEvenInvestment: number;
  /** PV of the selected year's avoided risk, discounted to the base year. */
  annualBreakEvenInvestment: number;
  /** First/last sampled years summed within [baseYear, selectedYear], for UI period labels. */
  periods: {
    startYear: number;
    selectedYear: number;
    breakEvenEndYear: number;
  };
};

/**
 * Σ (baseline − comparison) / (1 + rate)^(t − baseYear) over every year in [baseYear, endYear].
 * Expects annual (forward-filled) maps so no year is skipped; rate 0 gives the nominal total.
 */
const sumDiscountedAvoidedRisk = (
  baselineAnnual: Map<number, number>,
  comparisonAnnual: Map<number, number>,
  rate: number,
  baseYear: number,
  endYear: number
): number => {
  let total = 0;
  for (let year = baseYear; year <= endYear; year++) {
    const baseline = baselineAnnual.get(year);
    const comparison = comparisonAnnual.get(year);
    if (baseline === undefined || comparison === undefined) continue;
    total += (baseline - comparison) / Math.pow(1 + rate, year - baseYear);
  }
  return total;
};

export const computeAvoidedRiskMetrics = ({
  baselineSummary,
  comparisonSummary,
  rcp,
  hazard,
  selectedYear,
  discountRate,
  baseYear,
}: AvoidedRiskArgs): AvoidedRiskMetrics => {
  const baselineRow = findSummaryRow(baselineSummary, rcp, hazard);
  const comparisonRow = findSummaryRow(comparisonSummary, rcp, hazard);

  // Annual resolution so 5-yearly point-level data isn't undercounted in the cumulative totals.
  const rate = discountRate / 100;
  const baselineSamples = toSampleMap(baselineRow?.ttip ?? null);
  const comparisonSamples = toSampleMap(comparisonRow?.ttip ?? null);
  const baselineAnnual = forwardFillAnnual(baselineSamples);
  const comparisonAnnual = forwardFillAnnual(comparisonSamples);

  // Selected-year figures, taken from the pinned maps so the stats and the chart agree.
  const baselineTtip = baselineAnnual.get(selectedYear) ?? 0;
  const comparisonTtip = comparisonAnnual.get(selectedYear) ?? 0;
  const annualAvoidedRisk = baselineTtip - comparisonTtip;

  const marketValue = baselineRow?.total_market_value ?? null;
  const valueProtected = marketValue ? normalizeDisplayZero((annualAvoidedRisk / marketValue) * 100, "%") : null;

  const cumulativeAvoidedRisk = sumDiscountedAvoidedRisk(
    baselineAnnual,
    comparisonAnnual,
    0, // nominal — avoided risk is not discounted
    baseYear,
    selectedYear
  );
  const cumulativeBreakEvenInvestment = sumDiscountedAvoidedRisk(
    baselineAnnual,
    comparisonAnnual,
    rate,
    baseYear,
    selectedYear
  );

  // PV of the selected year's avoided risk; zero for years before the base year.
  const annualBreakEvenInvestment =
    selectedYear < baseYear ? 0 : annualAvoidedRisk / Math.pow(1 + rate, selectedYear - baseYear);

  // Sample years contributing to the sums, so the UI can label the period.
  const summedYears = [...baselineSamples.keys()]
    .filter((y) => comparisonAnnual.has(y) && y >= baseYear && y <= selectedYear)
    .sort((a, b) => a - b);

  const startYear = summedYears[0] ?? baseYear;

  return {
    annualAvoidedRisk,
    valueProtected,
    cumulativeAvoidedRisk,
    cumulativeBreakEvenInvestment,
    annualBreakEvenInvestment,
    periods: {
      startYear,
      selectedYear,
      breakEvenEndYear: summedYears[summedYears.length - 1] ?? selectedYear,
    },
  };
};

export type BreakEvenInvestmentSeriesRow = {
  year: number;
  /** Σ (baseline − comparison) / (1 + r)^(t − baseYear) over every year in [baseYear, year]. */
  cumulativeBreakevenInvestment: number;
};

type BreakEvenInvestmentSeriesArgs = {
  baselineSummary: PeaSummaryResponse;
  comparisonSummary: PeaSummaryResponse;
  rcp: Rcp;
  hazard: HazardPlusTotal;
  discountRate: number;
  baseYear: number;
};

/**
 * One row per plotted sample year (5-yearly for point-level, annual for aggregate). The running total
 * is forward-filled to annual, so at the selected year it matches computeAvoidedRiskMetrics.
 *
 * Runs from baseYear. Years with no comparison value are dropped, so a missing comparison series
 * yields an empty array for the chart's empty state.
 */
export const buildBreakEvenInvestmentSeries = ({
  baselineSummary,
  comparisonSummary,
  rcp,
  hazard,
  discountRate,
  baseYear,
}: BreakEvenInvestmentSeriesArgs): BreakEvenInvestmentSeriesRow[] => {
  const baselineRow = findSummaryRow(baselineSummary, rcp, hazard);
  const comparisonRow = findSummaryRow(comparisonSummary, rcp, hazard);

  const baselineSamples = toSampleMap(baselineRow?.ttip ?? null);
  const comparisonSamples = toSampleMap(comparisonRow?.ttip ?? null);
  const baselineAnnual = forwardFillAnnual(baselineSamples);
  const comparisonAnnual = forwardFillAnnual(comparisonSamples);
  const rate = discountRate / 100;

  // Running total per annual year, so each plotted row counts the in-between years point-level omits.
  const pvCumulativeByYear = new Map<number, number>();
  let cumulativeBreakevenInvestment = 0;
  for (const year of [...baselineAnnual.keys()].sort((a, b) => a - b)) {
    const baseline = baselineAnnual.get(year);
    const comparison = comparisonAnnual.get(year);
    if (baseline !== undefined && comparison !== undefined && year >= baseYear) {
      cumulativeBreakevenInvestment += (baseline - comparison) / Math.pow(1 + rate, year - baseYear);
    }
    pvCumulativeByYear.set(year, cumulativeBreakevenInvestment);
  }

  return [...baselineSamples.keys()]
    .filter((year) => year >= baseYear && comparisonAnnual.has(year))
    .sort((a, b) => a - b)
    .map((year) => ({ year, cumulativeBreakevenInvestment: pvCumulativeByYear.get(year) ?? 0 }));
};
